import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const VehicleDetailsCard = ({ vehicle, onUpdate, isEditing, onToggleEdit }) => {
  const [editData, setEditData] = useState({
    mileage: vehicle?.mileage || '',
    licensePlate: vehicle?.licensePlate || '',
    notes: vehicle?.notes || ''
  });

  const handleSave = () => {
    onUpdate(editData);
    onToggleEdit();
  };

  const handleCancel = () => {
    setEditData({
      mileage: vehicle?.mileage || '',
      licensePlate: vehicle?.licensePlate || '',
      notes: vehicle?.notes || ''
    });
    onToggleEdit();
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Icon name="Car" size={20} />
          Fahrzeugdetails
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleEdit}
          iconName={isEditing ? "X" : "Edit"}
          iconPosition="left"
        >
          {isEditing ? 'Abbrechen' : 'Bearbeiten'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vehicle Image */}
        <div className="space-y-4">
          <div className="aspect-video bg-muted rounded-lg overflow-hidden">
            <Image
              src={vehicle?.imageUrl || `https://images.unsplash.com/photo-1549924231-f129b911e442?w=400&h=300&fit=crop`}
              alt={`${vehicle?.make} ${vehicle?.model}`}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* VIN Photo */}
          {vehicle?.vinPhotoUrl && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-primary">VIN Foto</label>
              <div className="aspect-[4/3] bg-muted rounded-lg overflow-hidden">
                <Image
                  src={vehicle?.vinPhotoUrl}
                  alt="VIN Foto"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </div>

        {/* Vehicle Information */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-text-secondary">VIN</label>
              <div className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm">
                {vehicle?.vin}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-text-secondary">Marke</label>
              <div className="mt-1 p-3 bg-muted rounded-lg">
                {vehicle?.make}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-text-secondary">Modell</label>
              <div className="mt-1 p-3 bg-muted rounded-lg">
                {vehicle?.model}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-text-secondary">Baujahr</label>
              <div className="mt-1 p-3 bg-muted rounded-lg">
                {vehicle?.year}
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-4 pt-4 border-t border-border">
            {isEditing ? (
              <>
                <Input
                  label="Kilometerstand"
                  type="number"
                  value={editData?.mileage}
                  onChange={(e) => setEditData(prev => ({ ...prev, mileage: e?.target?.value }))}
                  placeholder="z.B. 45000"
                />
                
                <Input
                  label="Kennzeichen"
                  type="text"
                  value={editData?.licensePlate}
                  onChange={(e) => setEditData(prev => ({ ...prev, licensePlate: e?.target?.value }))}
                  placeholder="z.B. K-AB 1234"
                />
                
                <div>
                  <label className="text-sm font-medium text-text-primary mb-2 block">
                    Fahrzeugnotizen
                  </label>
                  <textarea
                    value={editData?.notes}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e?.target?.value }))}
                    placeholder="Zusätzliche Informationen zum Fahrzeug..."
                    className="w-full p-3 border border-input rounded-lg resize-none h-24 text-sm"
                  />
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="default"
                    onClick={handleSave}
                    iconName="Check"
                    iconPosition="left"
                  >
                    Speichern
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                  >
                    Abbrechen
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-text-secondary">Kilometerstand</label>
                    <div className="mt-1 p-3 bg-muted rounded-lg">
                      {vehicle?.mileage ? `${parseInt(vehicle?.mileage)?.toLocaleString('de-DE')} km` : 'Nicht angegeben'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-text-secondary">Kennzeichen</label>
                    <div className="mt-1 p-3 bg-muted rounded-lg">
                      {vehicle?.licensePlate || 'Nicht angegeben'}
                    </div>
                  </div>
                </div>
                
                {vehicle?.notes && (
                  <div>
                    <label className="text-sm font-medium text-text-secondary">Fahrzeugnotizen</label>
                    <div className="mt-1 p-3 bg-muted rounded-lg text-sm">
                      {vehicle?.notes}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailsCard;